import { themeAtom } from "../../shared/atoms";
import DataGridToolbar from "../../shared/components/DataGridToolbar";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import Dropdown from "../../shared/components/Dropdown/Dropdown";
import { toastError, toastSuccess } from "../../shared/components/ToastContainer";
import WithTooltip from "../../shared/components/WithTooltip";
import type { HullCleaningVendor } from "../models/hull-cleaning.models";
import type {
  CreateVesselDryDockInput,
  CreateVesselHullEventInput,
  PaintOption,
  VesselDryDock,
  VesselHullEvent,
} from "../models/uw-ops.models";
import { hullEventTypes, propellerCoatingTypes } from "../models/uw-ops.models";
import { getHullCleaningVendors } from "../services/hull-cleaning.service";
import {
  createVesselDryDock,
  createVesselHullEvent,
  deleteVesselDryDock,
  deleteVesselHullEvent,
  getHullCleaningPaintOptions,
  getVesselDryDocks,
  getVesselHullEvents,
  getUwOpsErrorMessage,
  updateVesselDryDock,
  updateVesselHullEvent,
} from "../services/uw-ops.service";
import { validateDryDockInput, validateHullEventInput } from "../utils/uw-ops.utils";
import { getVendorOptionsForLocation } from "../utils/vendor.utils";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  ArrowsUpDownIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PencilSquareIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

type MaintenanceTableColumn<T> = {
  key: keyof T;
  label: string;
  minWidth: number;
  format?: "date" | "eventTypeBadge";
};

const dryDockColumns: MaintenanceTableColumn<VesselDryDock>[] = [
  { key: "imoNumber", label: "IMO", minWidth: 110 },
  { key: "dryDockDate", label: "Dry Dock Date", minWidth: 130, format: "date" },
  { key: "antifoulingApplicationDate", label: "Antifouling Applied", minWidth: 150, format: "date" },
  { key: "nextPlannedDryDockDate", label: "Next Planned Dry Dock", minWidth: 170, format: "date" },
  { key: "place", label: "Place", minWidth: 140 },
  { key: "comments", label: "Comments", minWidth: 220 },
  { key: "paintVendor", label: "Paint Vendor", minWidth: 150 },
  { key: "flatBottomPaint", label: "Flat-Bottom Paint", minWidth: 170 },
  { key: "verticalSidePaint", label: "Vertical-Side Paint", minWidth: 170 },
  { key: "bootTopPaint", label: "Boot-Top Paint", minWidth: 160 },
  { key: "paintSchemePurpose", label: "Paint Scheme Service life(months)", minWidth: 220 },
  { key: "propellerCoatingType", label: "Propeller Coating", minWidth: 170 },
];

const hullEventColumns: MaintenanceTableColumn<VesselHullEvent>[] = [
  { key: "imoNumber", label: "IMO", minWidth: 110 },
  { key: "eventDate", label: "Date", minWidth: 130, format: "date" },
  { key: "eventType", label: "Event", minWidth: 170, format: "eventTypeBadge" },
  { key: "place", label: "Place", minWidth: 170 },
  { key: "vendor", label: "Vendor", minWidth: 180 },
  { key: "comments", label: "Comments", minWidth: 260 },
];

const hullEventSourceFilterOptions = [
  { value: "manual", label: "Manual" },
  { value: "workflow", label: "Workflow" },
];

const hullEventMatchesSource = (row: VesselHullEvent, value: string) =>
  value === "manual" ? !String(row.requestId ?? "").trim() : Boolean(String(row.requestId ?? "").trim());

const hullEventSourceBadgeClasses = {
  manual: "bg-[#f7dfe1] text-[#a33b4a] dark:bg-red-950/50 dark:text-red-300",
  workflow: "bg-[#dff3e7] text-[#2b8a57] dark:bg-emerald-950/50 dark:text-emerald-300",
} as const;

const getHullEventSourceBadgeClasses = (requestId?: string | null) =>
  String(requestId ?? "").trim() ? hullEventSourceBadgeClasses.workflow : hullEventSourceBadgeClasses.manual;

const emptyDryDock: CreateVesselDryDockInput = {
  dryDockDate: "",
  antifoulingApplicationDate: null,
  nextPlannedDryDockDate: null,
  place: null,
  comments: null,
  paintVendor: null,
  flatBottomPaint: null,
  verticalSidePaint: null,
  bootTopPaint: null,
  paintSchemePurpose: null,
  propellerCoatingType: null,
};

const emptyHullEvent: CreateVesselHullEventInput = {
  eventDate: "",
  eventType: "Hull Cleaning",
  place: null,
  vendor: null,
  comments: null,
};

const nullable = (value: string): string | null => value || null;

function RequiredAsterisk({ className }: { className?: string }) {
  return (
    <sup className={twMerge("ml-1 text-[#BF0002]", className)} aria-hidden="true">
      *
    </sup>
  );
}

const toDateInputValue = (value?: string | null): string => {
  if (!value) return "";
  const isoDate = value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (isoDate) return isoDate;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
};

const todayDateInputValue = (): string => {
  const today = new Date();
  const localToday = new Date(today.getTime() - today.getTimezoneOffset() * 60_000);
  return localToday.toISOString().slice(0, 10);
};

const toLocalDate = (value?: string | null): Date | null => {
  const normalized = toDateInputValue(value);
  if (!normalized) return null;
  const [year, month, day] = normalized.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const fromLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const dayAfter = (value?: string | null): Date | undefined => {
  const date = toLocalDate(value);
  if (!date) return undefined;
  date.setDate(date.getDate() + 1);
  return date;
};

function SectionToolbar({ title, isExpanded, onToggle }: { title: string; isExpanded: boolean; onToggle: () => void }) {
  const action = isExpanded ? "Collapse" : "Expand";
  const tooltipId = `hull-cleaning-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-tooltip`;
  return (
    <WithTooltip id={tooltipId} content={`${action} ${title}`} className="w-full" place="top">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={`${action} ${title}`}
        className="-mx-2 mb-4 flex w-[calc(100%+1rem)] cursor-pointer items-center justify-between gap-3 rounded-t-lg border-b px-2 py-2 text-left text-base font-bold text-green transition-colors hover:bg-primary-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-green/30 dark:hover:bg-gray-800"
      >
        <span>{title}</span>
        <ChevronDownIcon
          className={twMerge("h-5 w-5 shrink-0 transition-transform", !isExpanded && "-rotate-90")}
          aria-hidden="true"
        />
      </button>
    </WithTooltip>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="m-5 flex flex-col items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200 sm:m-6 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div>{message}</div>
      </div>
      <button type="button" onClick={onRetry} className="font-semibold underline underline-offset-2">
        Retry
      </button>
    </div>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="space-y-3 p-5 sm:p-6" role="status" aria-label={label}>
      <div className="h-10 animate-pulse rounded-lg bg-greyscale-10 dark:bg-gray-700" />
      {[0, 1, 2].map((row) => (
        <div key={row} className="h-11 animate-pulse rounded-lg bg-gray-50 dark:bg-gray-800" />
      ))}
      <span className="sr-only">{label}</span>
    </div>
  );
}

function EmptyState({ kind, onAdd }: { kind: "dry-dock" | "hull-event"; onAdd?: () => void }) {
  const isDryDock = kind === "dry-dock";
  const EmptyIcon = isDryDock ? CalendarDaysIcon : SparklesIcon;
  return (
    <div className="m-5 flex min-h-[230px] flex-col items-center justify-center rounded-xl border border-dashed border-[#B8D4CF] bg-[#FBFDFC] px-6 py-10 text-center dark:border-gray-600 dark:bg-gray-800/40 sm:m-6">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-10 text-green dark:bg-teal-950/50">
        <EmptyIcon className="h-7 w-7" aria-hidden="true" />
      </div>
      <h4 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
        {isDryDock ? "No dry-dock records yet" : "No hull events yet"}
      </h4>
      <p className="mt-1 max-w-md text-sm leading-6 text-gray-500 dark:text-gray-400">
        {isDryDock
          ? "Add the vessel's first dry-dock record, including coating dates and paint details."
          : onAdd
            ? "Add the first cleaning, inspection, or propeller-polishing event for this vessel."
            : "No cleaning, inspection, or propeller-polishing events are available for this vessel."}
      </p>
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="mt-5 inline-flex h-10 items-center gap-2 rounded-md bg-green px-4 text-sm font-semibold text-white shadow-sm hover:bg-[#176a5e]"
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          Add first record
        </button>
      )}
    </div>
  );
}

function RowActionsMenu({
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
}: {
  onEdit: () => void;
  onDelete: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
}) {
  const itemClass =
    "group flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors data-[headlessui-state=active]:bg-primary-10 data-[headlessui-state=active]:text-green dark:text-gray-200 dark:data-[headlessui-state=active]:bg-gray-700";

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        type="button"
        aria-label="Open row actions"
        onClick={(event) => event.stopPropagation()}
        className="border-greyscale-20 inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-lightGrey transition-colors hover:bg-primary-10 hover:text-green focus:outline-none focus:ring-2 focus:ring-green/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
      >
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="border-greyscale-20 absolute right-0 z-30 mt-2 w-40 origin-top-right overflow-hidden rounded-md border bg-white py-1 shadow-lg focus:outline-none dark:border-gray-600 dark:bg-gray-800">
          <Menu.Item>
            <button
              type="button"
              onClick={onEdit}
              disabled={!canEdit}
              title={canEdit ? "Edit record" : "Editing is unavailable"}
              className={twMerge(itemClass, "disabled:cursor-not-allowed disabled:opacity-45")}
            >
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
              Edit
            </button>
          </Menu.Item>
          <Menu.Item>
            <button
              type="button"
              onClick={onDelete}
              disabled={!canDelete}
              title={canDelete ? "Delete record" : "Request-linked events cannot be deleted"}
              className={twMerge(
                itemClass,
                "text-[#BF0002] disabled:cursor-not-allowed disabled:opacity-45 data-[headlessui-state=active]:bg-[#FFF1F1] data-[headlessui-state=active]:text-[#BF0002]",
              )}
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
              Delete
            </button>
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function MaintenanceTable<T extends object>({
  rows,
  columns,
  filterKey,
  filterLabel,
  configuredFilterOptions,
  allFilterLabel,
  filterPredicate,
  getRowId,
  onAdd,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  showActions = true,
  addLabel,
  dataTest,
  className,
}: {
  rows: T[];
  columns: MaintenanceTableColumn<T>[];
  filterKey: keyof T;
  filterLabel: string;
  configuredFilterOptions?: Array<{ value: string; label: string }>;
  allFilterLabel?: string;
  filterPredicate?: (row: T, filterValue: string) => boolean;
  getRowId: (row: T) => string;
  onAdd?: () => void;
  onEdit: (row: T) => void;
  onDelete: (row: T) => void;
  canEdit?: (row: T) => boolean;
  canDelete?: (row: T) => boolean;
  showActions?: boolean;
  addLabel: string;
  dataTest: string;
  className?: string;
}) {
  const theme = useAtomValue(themeAtom);
  const [search, setSearch] = useState("");
  const [filterValue, setFilterValue] = useState("all");
  const initialSortKey = String(columns.find((column) => column.format === "date")?.key ?? columns[0]?.key ?? "");
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const filterOptions = useMemo(() => {
    if (configuredFilterOptions) return configuredFilterOptions;
    return Array.from(new Set(rows.map((row) => String(row[filterKey] ?? "").trim()).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
      .map((option) => ({ value: option, label: option }));
  }, [configuredFilterOptions, filterKey, rows]);
  const visibleRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filteredRows = rows.filter(
      (row) =>
        (filterValue === "all" ||
          (filterPredicate ? filterPredicate(row, filterValue) : String(row[filterKey] ?? "") === filterValue)) &&
        (!normalizedSearch ||
          columns.some((column) =>
            String(row[column.key] ?? "")
              .toLowerCase()
              .includes(normalizedSearch),
          )),
    );
    const sortColumn = columns.find((column) => String(column.key) === sortKey);
    if (!sortColumn) return filteredRows;

    return [...filteredRows].sort((left, right) => {
      const leftValue = String(left[sortColumn.key] ?? "");
      const rightValue = String(right[sortColumn.key] ?? "");
      const comparison = leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: "base" });
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [columns, filterKey, filterPredicate, filterValue, rows, search, sortDirection, sortKey]);

  const toggleSort = (columnKey: keyof T) => {
    const nextSortKey = String(columnKey);
    if (sortKey === nextSortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextSortKey);
    setSortDirection("asc");
  };

  const resetGrid = () => {
    setSearch("");
    setFilterValue("all");
    setSortKey(initialSortKey);
    setSortDirection("desc");
  };

  return (
    <div className={twMerge("flex min-h-0 flex-col gap-3", className)} data-test={dataTest}>
      <DataGridToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchAriaLabel="Search records"
        filterValue={filterValue}
        onFilterChange={setFilterValue}
        filterAriaLabel={`Filter by ${filterLabel}`}
        allFilterLabel={allFilterLabel ?? `All ${filterLabel}`}
        filterOptions={filterOptions}
        resultCount={visibleRows.length}
        totalCount={rows.length}
        resultLabel="records"
        onReset={resetGrid}
        resetAriaLabel="Reset grid to its initial state"
        onAdd={onAdd}
        addLabel={addLabel}
      />
      <div
        className={twMerge(
          "min-h-0 flex-1 overflow-auto rounded-lg border [scrollbar-color:#B7C9C4_#F3F4F4] dark:[scrollbar-color:#4B5563_#1F2937] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-[#F3F4F4] dark:[&::-webkit-scrollbar-track]:bg-gray-800 [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar]:w-2.5",
          theme.myVesselsTable.tailwind.tableBorder,
          theme.uwhcAssistant.tableScroll,
          theme.uwhcAssistant.scrolHover,
        )}
      >
        <table className={twMerge("min-w-full text-[12px]", theme.vesselDetails.info)}>
          <thead className="sticky top-0 z-10 text-[#2F7F7B] dark:text-teal-300">
            <tr
              className={twMerge(
                "border-b",
                theme.myVesselsTable.tailwind.tableBorder,
                theme.uwhcAssistant.chatUi.headerBg,
                theme.uwhcAssistant.chatUi.panelBorder,
              )}
            >
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  scope="col"
                  className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                  style={{ minWidth: column.minWidth }}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(column.key)}
                    className="group inline-flex items-center gap-1.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-green/30"
                  >
                    {column.label}
                    {sortKey === String(column.key) ? (
                      sortDirection === "asc" ? (
                        <ChevronUpIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      )
                    ) : (
                      <ArrowsUpDownIcon
                        className="h-3.5 w-3.5 opacity-35 transition group-hover:opacity-80"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </th>
              ))}
              {showActions && (
                <th
                  scope="col"
                  className={twMerge(
                    "sticky right-0 z-20 w-14 border-l px-3 py-3 text-right text-xs font-semibold uppercase shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)]",
                    theme.myVesselsTable.tailwind.tableBorder,
                    theme.uwhcAssistant.chatUi.headerBg,
                  )}
                >
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const rowId = getRowId(row);
              return (
                <tr
                  key={rowId}
                  data-test={`${dataTest}-row-${rowId}`}
                  className={twMerge(
                    "group border-b text-left transition-colors last:border-b-0 hover:bg-primary-10",
                    theme.myVesselsTable.tailwind.tableBorder,
                    theme.vesselDetails.info,
                  )}
                >
                  {columns.map((column) => {
                    const value = row[column.key];
                    const formattedValue =
                      column.format === "date" && typeof value === "string" ? toDateInputValue(value) : value;
                    return (
                      <td key={String(column.key)} className="px-4 py-3 align-top text-sm">
                        {column.format === "eventTypeBadge" && formattedValue != null && formattedValue !== "" ? (
                          <span
                            className={twMerge(
                              "inline-block rounded-md px-[10px] py-[5px] text-xs font-bold leading-4",
                              getHullEventSourceBadgeClasses((row as { requestId?: string | null }).requestId),
                            )}
                          >
                            {String(formattedValue)}
                          </span>
                        ) : (
                          <span
                            className={twMerge(formattedValue == null || formattedValue === "" ? "text-gray-400" : "")}
                          >
                            {formattedValue == null || formattedValue === "" ? "-" : String(formattedValue)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  {showActions && (
                    <td
                      className={twMerge(
                        "sticky right-0 z-10 w-14 border-l px-3 py-2 text-right align-middle shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)] transition-colors group-hover:bg-primary-10",
                        theme.myVesselsTable.tailwind.tableBorder,
                        theme.uwhcAssistant.chatUi.panelBg,
                      )}
                    >
                      <RowActionsMenu
                        onEdit={() => onEdit(row)}
                        onDelete={() => onDelete(row)}
                        canEdit={canEdit?.(row) ?? true}
                        canDelete={canDelete?.(row) ?? true}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
            {visibleRows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + (showActions ? 1 : 0)}
                  className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  No matching records.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormField({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  required = false,
}: {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  type?: "text" | "date" | "number";
  disabled?: boolean;
  required?: boolean;
}) {
  const handleChange = (nextValue: string) => {
    if (type === "number" && nextValue && (!/^\d{1,3}$/.test(nextValue) || /^0+$/.test(nextValue))) return;
    onChange(nextValue);
  };

  return (
    <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
      <span>
        {label}
        {required && <RequiredAsterisk />}
      </span>
      <input
        type={type}
        min={type === "number" ? 1 : undefined}
        max={type === "number" ? 999 : undefined}
        step={type === "number" ? 1 : undefined}
        inputMode={type === "number" ? "numeric" : undefined}
        value={value ?? ""}
        onChange={(event) => handleChange(event.target.value)}
        disabled={disabled}
        required={required}
        className="h-11 rounded-md border border-gray-300 bg-white px-3 text-sm font-normal text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-green focus:outline-none focus:ring-2 focus:ring-green/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:disabled:bg-gray-800"
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
  disabled = false,
  required = false,
}: {
  label: string;
  value: string | null;
  options: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
}) {
  return (
    <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
      <span>
        {label}
        {required && <RequiredAsterisk />}
      </span>
      <select
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        required={required}
        className="h-11 rounded-md border border-gray-300 bg-white px-3 text-sm font-normal text-gray-900 shadow-sm focus:border-green focus:outline-none focus:ring-2 focus:ring-green/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function DateField({
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  minDate,
  maxDate,
}: {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
}) {
  return (
    <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
      <span>
        {label}
        {required && <RequiredAsterisk />}
      </span>
      <Datepicker
        selected={toLocalDate(value)}
        onChange={(date) => onChange(fromLocalDate(date))}
        isDisabled={disabled}
        dateFormat="MM/dd/yyyy"
        placeholderText={`Select ${label.toLowerCase()}`}
        {...(minDate ? { minDate } : {})}
        {...(maxDate ? { maxDate } : {})}
      />
    </label>
  );
}

function DropdownField({
  label,
  value,
  options,
  onChange,
  disabled = false,
  required = false,
  searchable = false,
}: {
  label: string;
  value: string | null;
  options: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  searchable?: boolean;
}) {
  return (
    <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
      <span>
        {label}
        {required && <RequiredAsterisk />}
      </span>
      <Dropdown
        value={value ?? ""}
        options={options}
        placeholder={searchable ? "Search or select" : "Select"}
        disabled={disabled}
        readOnly={!searchable}
        onSelectOption={onChange}
        ariaLabel={label}
        className="[&>input]:min-w-0 [&>input]:rounded-md [&>input]:px-3 [&>input]:py-2 [&>input]:text-sm"
      />
    </label>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 md:col-span-2">
      {label}
      <textarea
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-[100px] resize-y rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm font-normal text-gray-900 shadow-sm focus:border-green focus:outline-none focus:ring-2 focus:ring-green/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
      />
    </label>
  );
}

function EditorDialog({
  open,
  title,
  saving,
  error,
  onClose,
  onSave,
  children,
}: {
  open: boolean;
  title: string;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: () => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} className="relative z-[1000]">
      <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-[2px]" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
        <div className="flex min-h-full items-center justify-center">
          <Dialog.Panel className="w-full max-w-4xl overflow-hidden rounded-2xl border border-[#D7E2DE] bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800">
            <div className="border-greyscale-20 flex items-start justify-between gap-4 border-b bg-[#F7FBFA] px-5 py-5 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
              <div>
                <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white">{title}</Dialog.Title>
                <div className="mt-3 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-full bg-primary-10 px-3 py-1.5 text-xs font-medium text-green dark:bg-teal-950/50 dark:text-teal-200">
                  <InformationCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>Complete the details below</span>
                  <span className="text-[#8AA7A2] dark:text-gray-500" aria-hidden="true">
                    •
                  </span>
                  <span>
                    <RequiredAsterisk className="ml-0" /> Required fields
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                aria-label="Close dialog"
                className="border-greyscale-20 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-white text-gray-500 transition-colors hover:bg-greyscale-10 hover:text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            {error && (
              <div
                role="alert"
                className="mx-5 mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200 sm:mx-6"
              >
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}
            <div className="grid grid-cols-1 gap-x-5 gap-y-5 px-5 py-6 sm:px-6 md:grid-cols-2">{children}</div>
            <div className="border-greyscale-20 flex flex-col-reverse gap-3 border-t bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/40 sm:flex-row sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="inline-flex h-11 items-center justify-center rounded-md border border-gray-300 bg-white px-5 text-sm font-semibold text-lightGrey transition-colors hover:bg-greyscale-10 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-green px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#176a5e] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                )}
                {saving ? "Saving..." : "Save record"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}

function ConfirmDeleteDialog({
  open,
  title,
  description,
  deleting,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={() => !deleting && onClose()} className="relative z-[1000]">
      <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-[2px]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-2xl border border-red-100 bg-white shadow-2xl dark:border-red-900/50 dark:bg-gray-800">
          <div className="p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-[#BF0002] dark:bg-red-950/50">
              <TrashIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <Dialog.Title className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">{title}</Dialog.Title>
            <Dialog.Description className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {description} This action cannot be undone.
            </Dialog.Description>
          </div>
          <div className="border-greyscale-20 flex flex-col-reverse gap-3 border-t bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/40 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={deleting}
              className="inline-flex h-11 items-center justify-center rounded-md border border-gray-300 bg-white px-5 text-sm font-semibold text-lightGrey dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={deleting}
              className="inline-flex h-11 items-center justify-center rounded-md bg-[#BF0002] px-5 text-sm font-semibold text-white shadow-sm hover:bg-[#9f0002] disabled:opacity-50"
            >
              {deleting && <ArrowPathIcon className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />}
              {deleting ? "Deleting..." : "Delete record"}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function DryDockDialog({
  open,
  record,
  rows,
  paints,
  paintsLoading,
  paintsError,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  record: VesselDryDock | null;
  rows: VesselDryDock[];
  paints: PaintOption[];
  paintsLoading: boolean;
  paintsError: string | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (value: CreateVesselDryDockInput) => void;
}) {
  const [form, setForm] = useState<CreateVesselDryDockInput>(emptyDryDock);
  const [validationError, setValidationError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setForm(
      record
        ? {
            ...record,
            dryDockDate: toDateInputValue(record.dryDockDate),
            antifoulingApplicationDate: toDateInputValue(record.antifoulingApplicationDate) || null,
            nextPlannedDryDockDate: toDateInputValue(record.nextPlannedDryDockDate) || null,
          }
        : emptyDryDock,
    );
    setValidationError(null);
  }, [open, record]);
  const vendors = useMemo(() => Array.from(new Set(paints.map((row) => row.vendor).filter(Boolean))).sort(), [paints]);
  const products = useMemo(
    () => Array.from(new Set(paints.filter((row) => row.vendor === form.paintVendor).map((row) => row.paint))).sort(),
    [form.paintVendor, paints],
  );
  const set = (key: keyof CreateVesselDryDockInput, value: string | null) =>
    setForm((current) => ({ ...current, [key]: value }));
  const save = () => {
    const nextError = validateDryDockInput(form, rows, Boolean(record));
    if (nextError) return setValidationError(nextError);
    setValidationError(null);
    onSave(form);
  };
  return (
    <EditorDialog
      open={open}
      title={record ? "Edit dry-dock record" : "Add dry-dock record"}
      saving={saving}
      error={validationError ?? error}
      onClose={onClose}
      onSave={save}
    >
      <DateField
        label="Dry-dock date"
        required
        value={form.dryDockDate}
        disabled={Boolean(record)}
        maxDate={toLocalDate(form.antifoulingApplicationDate) ?? undefined}
        onChange={(value) => set("dryDockDate", value)}
      />
      <DateField
        label="Antifouling application date"
        required
        value={form.antifoulingApplicationDate}
        minDate={toLocalDate(form.dryDockDate) ?? undefined}
        onChange={(value) => set("antifoulingApplicationDate", value || null)}
      />
      <DateField
        label="Next planned dry-dock date"
        required
        value={form.nextPlannedDryDockDate}
        minDate={dayAfter(form.dryDockDate)}
        onChange={(value) => set("nextPlannedDryDockDate", value || null)}
      />
      <FormField label="Place" required value={form.place} onChange={(value) => set("place", nullable(value))} />
      {(paintsLoading || paintsError || paints.length === 0) && (
        <div
          role={paintsError ? "alert" : "status"}
          className={twMerge(
            "rounded-lg border px-3 py-2 text-xs md:col-span-2",
            paintsError
              ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
              : "border-[#C9DFDC] bg-[#EEF8F6] text-[#0F5F54] dark:border-teal-900 dark:bg-teal-950/40 dark:text-teal-100",
          )}
        >
          {paintsLoading
            ? "Loading maintained paint vendors and products..."
            : paintsError
              ? paintsError
              : "No maintained paint combinations are available. Add them in Admin > Paints Scheme."}
        </div>
      )}
      <DropdownField
        label="Paint vendor"
        required
        value={form.paintVendor}
        options={vendors}
        disabled={paintsLoading || Boolean(paintsError) || paints.length === 0}
        onChange={(value) =>
          setForm((current) => ({
            ...current,
            paintVendor: value || null,
            flatBottomPaint: null,
            verticalSidePaint: null,
            bootTopPaint: null,
          }))
        }
      />
      <DropdownField
        label="Flat-bottom paint"
        required
        searchable
        value={form.flatBottomPaint}
        options={products}
        disabled={!form.paintVendor}
        onChange={(value) => set("flatBottomPaint", value || null)}
      />
      <DropdownField
        label="Vertical-side paint"
        required
        searchable
        value={form.verticalSidePaint}
        options={products}
        disabled={!form.paintVendor}
        onChange={(value) => set("verticalSidePaint", value || null)}
      />
      <DropdownField
        label="Boot-top paint"
        required
        searchable
        value={form.bootTopPaint}
        options={products}
        disabled={!form.paintVendor}
        onChange={(value) => set("bootTopPaint", value || null)}
      />
      <FormField
        label="Paint Scheme Service life(months)"
        type="number"
        value={form.paintSchemePurpose}
        onChange={(value) => set("paintSchemePurpose", nullable(value))}
      />
      <DropdownField
        label="Propeller coating"
        value={form.propellerCoatingType}
        options={[...propellerCoatingTypes]}
        onChange={(value) => set("propellerCoatingType", value || null)}
      />
      <TextAreaField
        label="Comments"
        value={form.comments}
        onChange={(value) => set("comments", value === "" ? null : value)}
      />
    </EditorDialog>
  );
}

function HullEventDialog({
  open,
  record,
  rows,
  vendors,
  vendorsLoading,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  record: VesselHullEvent | null;
  rows: VesselHullEvent[];
  vendors: HullCleaningVendor[];
  vendorsLoading: boolean;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (value: CreateVesselHullEventInput) => void;
}) {
  const [form, setForm] = useState<CreateVesselHullEventInput>(emptyHullEvent);
  const [validationError, setValidationError] = useState<string | null>(null);
  const vendorOptions = useMemo(() => getVendorOptionsForLocation(vendors, form.place ?? ""), [form.place, vendors]);
  useEffect(() => {
    if (!open) return;
    setForm(
      record
        ? { ...record, eventDate: toDateInputValue(record.eventDate) }
        : { ...emptyHullEvent, eventDate: todayDateInputValue() },
    );
    setValidationError(null);
  }, [open, record]);
  const set = (key: keyof CreateVesselHullEventInput, value: string | null) =>
    setForm((current) => ({ ...current, [key]: value }));
  const save = () => {
    const nextError = validateHullEventInput(form, rows, Boolean(record));
    if (nextError) return setValidationError(nextError);
    setValidationError(null);
    onSave(form);
  };
  return (
    <EditorDialog
      open={open}
      title={record ? "Edit Hull Event" : "Add Hull Event"}
      saving={saving}
      error={validationError ?? error}
      onClose={onClose}
      onSave={save}
    >
      <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
        <span>
          Event date
          <RequiredAsterisk />
        </span>
        <Datepicker
          selected={toLocalDate(form.eventDate)}
          onChange={(date) => set("eventDate", fromLocalDate(date))}
          isDisabled={Boolean(record)}
          dateFormat="MM/dd/yyyy"
          placeholderText="Select event date"
        />
      </label>
      <SelectField
        label="Event type"
        required
        value={form.eventType}
        options={[...hullEventTypes]}
        disabled={Boolean(record)}
        onChange={(value) => set("eventType", value)}
      />
      <FormField label="Place" value={form.place} onChange={(value) => set("place", nullable(value))} />
      <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
        Vendor
        <Dropdown
          value={form.vendor ?? ""}
          options={vendorOptions}
          placeholder={form.place ? "Enter or select a vendor" : "Enter a place to find vendors"}
          isLoading={vendorsLoading}
          onChange={(event) => {
            const value = (event.target as HTMLInputElement).value;
            if (typeof value === "string") set("vendor", nullable(value));
          }}
          onSelectOption={(option) => set("vendor", option)}
          ariaLabel="Vendor"
          className="[&>input]:min-w-0 [&>input]:rounded-md [&>input]:px-3 [&>input]:py-2 [&>input]:text-sm"
        />
      </label>
      <TextAreaField
        label="Comments"
        value={form.comments}
        onChange={(value) => set("comments", value === "" ? null : value)}
      />
    </EditorDialog>
  );
}

function DryDocksSection({
  imoNumber,
  paints,
  paintsLoading,
  paintsError,
}: {
  imoNumber: string;
  paints: PaintOption[];
  paintsLoading: boolean;
  paintsError: string | null;
}) {
  const theme = useAtomValue(themeAtom);
  const [rows, setRows] = useState<VesselDryDock[]>([]);
  const [selected, setSelected] = useState<VesselDryDock | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"add" | "edit" | "delete" | null>(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await getVesselDryDocks(imoNumber));
      setSelected(null);
    } catch (reason) {
      setError(getUwOpsErrorMessage(reason, "Unable to load dry-dock records."));
    } finally {
      setLoading(false);
    }
  }, [imoNumber]);
  useEffect(() => {
    void load();
  }, [load]);
  const selectedRecord = selected;
  const saveDialog = async (value: CreateVesselDryDockInput) => {
    setSaving(true);
    setDialogError(null);
    try {
      if (dialog === "edit" && selectedRecord) {
        const { dryDockDate: _, ...changes } = value;
        await updateVesselDryDock(imoNumber, selectedRecord.dryDockDate, changes);
        toastSuccess("Dry-dock record updated.");
      } else {
        await createVesselDryDock(imoNumber, value);
        toastSuccess("Dry-dock record added.");
      }
      setDialog(null);
      await load();
    } catch (reason) {
      setDialogError(getUwOpsErrorMessage(reason, "Unable to save the dry-dock record."));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!selectedRecord) return;
    setSaving(true);
    try {
      await deleteVesselDryDock(imoNumber, selectedRecord.dryDockDate);
      toastSuccess("Dry-dock record permanently deleted.");
      setDialog(null);
      await load();
    } catch (reason) {
      toastError(getUwOpsErrorMessage(reason, "Unable to delete the dry-dock record."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section
      className={twMerge(
        "rounded-lg p-6 shadow-md",
        theme.home.tailwind.panelBackground,
        theme.home.tailwind.textDefault,
      )}
    >
      <SectionToolbar title="Dry Docks" isExpanded={isExpanded} onToggle={() => setIsExpanded((current) => !current)} />
      {isExpanded && (
        <div className="h-[500px]">
          {error ? (
            <ErrorState message={error} onRetry={() => void load()} />
          ) : loading ? (
            <LoadingState label="Loading dry-dock records" />
          ) : rows.length === 0 ? (
            <EmptyState
              kind="dry-dock"
              onAdd={() => {
                setDialogError(null);
                setDialog("add");
              }}
            />
          ) : (
            <MaintenanceTable
              className="h-full"
              rows={rows}
              columns={dryDockColumns}
              filterKey="paintVendor"
              filterLabel="paint vendors"
              addLabel="Add a Dry Dock"
              dataTest="vessel-dry-docks-table"
              getRowId={(row) => `${row.imoNumber}__${row.dryDockDate}`}
              onAdd={() => {
                setSelected(null);
                setDialogError(null);
                setDialog("add");
              }}
              onEdit={(row) => {
                setSelected(row);
                setDialogError(null);
                setDialog("edit");
              }}
              onDelete={(row) => {
                setSelected(row);
                setDialog("delete");
              }}
            />
          )}
        </div>
      )}
      <DryDockDialog
        open={dialog === "add" || dialog === "edit"}
        record={dialog === "edit" ? selectedRecord : null}
        rows={rows}
        paints={paints}
        paintsLoading={paintsLoading}
        paintsError={paintsError}
        saving={saving}
        error={dialogError}
        onClose={() => !saving && setDialog(null)}
        onSave={(value) => void saveDialog(value)}
      />
      <ConfirmDeleteDialog
        open={dialog === "delete"}
        title="Delete dry-dock record?"
        description={
          selectedRecord
            ? `This permanently deletes the ${selectedRecord.dryDockDate} dry-dock record for IMO ${selectedRecord.imoNumber}.`
            : ""
        }
        deleting={saving}
        onClose={() => setDialog(null)}
        onConfirm={() => void remove()}
      />
    </section>
  );
}

function HullEventsSection({
  imoNumber,
  vendors,
  vendorsLoading,
}: {
  imoNumber: string;
  vendors: HullCleaningVendor[];
  vendorsLoading: boolean;
}) {
  const theme = useAtomValue(themeAtom);
  const [rows, setRows] = useState<VesselHullEvent[]>([]);
  const [selected, setSelected] = useState<VesselHullEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"add" | "edit" | "delete" | null>(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await getVesselHullEvents(imoNumber));
      setSelected(null);
    } catch (reason) {
      setError(getUwOpsErrorMessage(reason, "Unable to load hull events."));
    } finally {
      setLoading(false);
    }
  }, [imoNumber]);
  useEffect(() => {
    void load();
  }, [load]);
  const selectedRecord = selected;
  const saveDialog = async (value: CreateVesselHullEventInput) => {
    setSaving(true);
    setDialogError(null);
    try {
      if (dialog === "edit" && selectedRecord) {
        await updateVesselHullEvent(imoNumber, selectedRecord.eventId, {
          place: value.place,
          vendor: value.vendor,
          comments: value.comments,
        });
        toastSuccess("Hull event updated.");
      } else {
        await createVesselHullEvent(imoNumber, value);
        toastSuccess("Hull event added.");
      }
      setDialog(null);
      await load();
    } catch (reason) {
      setDialogError(getUwOpsErrorMessage(reason, "Unable to save the hull event."));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!selectedRecord) return;
    setSaving(true);
    try {
      await deleteVesselHullEvent(imoNumber, selectedRecord.eventId);
      toastSuccess("Hull event permanently deleted.");
      setDialog(null);
      await load();
    } catch (reason) {
      toastError(getUwOpsErrorMessage(reason, "Unable to delete the hull event."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section
      className={twMerge(
        "rounded-lg p-6 shadow-md",
        theme.home.tailwind.panelBackground,
        theme.home.tailwind.textDefault,
      )}
    >
      <SectionToolbar
        title="Hull Events"
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded((current) => !current)}
      />
      {isExpanded && (
        <div className="h-[500px]">
          {error ? (
            <ErrorState message={error} onRetry={() => void load()} />
          ) : loading ? (
            <LoadingState label="Loading hull events" />
          ) : rows.length === 0 ? (
            <EmptyState
              kind="hull-event"
              onAdd={() => {
                setDialogError(null);
                setDialog("add");
              }}
            />
          ) : (
            <div className="flex h-full min-h-0 flex-col">
              <aside
                aria-label="Hull event source legend"
                data-test="hull-event-source-legend"
                className="mb-3 ml-auto flex w-full flex-col gap-2 rounded-xl border border-[#CFE1DD] bg-gradient-to-r from-[#F2F8F6] to-white px-3 py-2.5 shadow-sm dark:border-gray-700 dark:from-gray-800 dark:to-gray-800/70 sm:w-1/2 lg:w-1/3 xl:w-1/6"
              >
                <WithTooltip
                  id="hull-event-source-legend-heading-tooltip"
                  content="Indicates whether a hull event was added manually or created by the hull-cleaning workflow."
                  contentMode="text"
                  className="w-full"
                  place="left"
                >
                  <div
                    tabIndex={0}
                    className="flex min-w-0 items-center gap-2.5 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-green/30"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-green shadow-sm ring-1 ring-[#D8E8E4] dark:bg-gray-900 dark:ring-gray-700">
                      <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-green">Event source</p>
                  </div>
                </WithTooltip>
                <div role="list" className="flex flex-wrap items-center gap-2">
                  <div role="listitem">
                    <WithTooltip
                      id="hull-event-source-manual-tooltip"
                      content="The hull event was added directly by a user."
                      contentMode="text"
                      className="inline-flex"
                      place="left"
                    >
                      <div
                        tabIndex={0}
                        className="inline-flex items-center gap-2 rounded-lg border border-[#E8C9CD] bg-white px-2.5 py-1.5 text-xs shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[#A33B4A]/40 dark:border-red-900/70 dark:bg-gray-900"
                      >
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#A33B4A] ring-4 ring-[#F7DFE1] dark:ring-red-950/70"
                          aria-hidden="true"
                        />
                        <span className="font-semibold text-gray-800 dark:text-gray-100">Manual</span>
                      </div>
                    </WithTooltip>
                  </div>
                  <div role="listitem">
                    <WithTooltip
                      id="hull-event-source-workflow-tooltip"
                      content="The hull event was created from a hull-cleaning request."
                      contentMode="text"
                      className="inline-flex"
                      place="left"
                    >
                      <div
                        tabIndex={0}
                        className="inline-flex items-center gap-2 rounded-lg border border-[#C5E3D1] bg-white px-2.5 py-1.5 text-xs shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[#2B8A57]/40 dark:border-emerald-900/70 dark:bg-gray-900"
                      >
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#2B8A57] ring-4 ring-[#DFF3E7] dark:ring-emerald-950/70"
                          aria-hidden="true"
                        />
                        <span className="font-semibold text-gray-800 dark:text-gray-100">Workflow</span>
                      </div>
                    </WithTooltip>
                  </div>
                </div>
              </aside>
              <MaintenanceTable
                className="min-h-0 flex-1"
                rows={rows}
                columns={hullEventColumns}
                filterKey="requestId"
                filterLabel="sources"
                allFilterLabel="All sources"
                configuredFilterOptions={hullEventSourceFilterOptions}
                filterPredicate={hullEventMatchesSource}
                addLabel="Add a new Hull Event"
                dataTest="vessel-hull-events-table"
                getRowId={(row) => row.eventId}
                onAdd={() => {
                  setSelected(null);
                  setDialogError(null);
                  setDialog("add");
                }}
                showActions
                onEdit={(row) => {
                  setSelected(row);
                  setDialogError(null);
                  setDialog("edit");
                }}
                onDelete={(row) => {
                  setSelected(row);
                  setDialog("delete");
                }}
              />
            </div>
          )}
        </div>
      )}
      <HullEventDialog
        open={dialog === "add" || dialog === "edit"}
        record={dialog === "edit" ? selectedRecord : null}
        rows={rows}
        vendors={vendors}
        vendorsLoading={vendorsLoading}
        saving={saving}
        error={dialogError}
        onClose={() => !saving && setDialog(null)}
        onSave={(value) => void saveDialog(value)}
      />
      <ConfirmDeleteDialog
        open={dialog === "delete"}
        title="Delete Hull Event?"
        description={
          selectedRecord
            ? `This permanently deletes the ${selectedRecord.eventType} event dated ${selectedRecord.eventDate}.`
            : ""
        }
        deleting={saving}
        onClose={() => setDialog(null)}
        onConfirm={() => void remove()}
      />
    </section>
  );
}

export default function UwOpsSections({ imoNumber }: { imoNumber: string }) {
  const [paints, setPaints] = useState<PaintOption[]>([]);
  const [paintsLoading, setPaintsLoading] = useState(true);
  const [paintsError, setPaintsError] = useState<string | null>(null);
  const [vendors, setVendors] = useState<HullCleaningVendor[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setPaintsLoading(true);
    setPaintsError(null);
    getHullCleaningPaintOptions()
      .then((options) => active && setPaints(options))
      .catch((error) => {
        if (active) {
          setPaints([]);
          setPaintsError(getUwOpsErrorMessage(error, "Unable to load maintained paint options."));
        }
        console.error("Unable to load paint options", error);
      })
      .finally(() => active && setPaintsLoading(false));
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    setVendorsLoading(true);
    getHullCleaningVendors()
      .then((options) => active && setVendors(options))
      .catch((error) => {
        if (active) setVendors([]);
        console.error("Unable to load hull-cleaning vendors", error);
      })
      .finally(() => active && setVendorsLoading(false));
    return () => {
      active = false;
    };
  }, []);
  return (
    <div className="flex flex-col gap-4" data-test="uw-ops-maintenance">
      <DryDocksSection imoNumber={imoNumber} paints={paints} paintsLoading={paintsLoading} paintsError={paintsError} />
      <HullEventsSection imoNumber={imoNumber} vendors={vendors} vendorsLoading={vendorsLoading} />
    </div>
  );
}
